/* Phase 68 — Searchable + virtualized combobox for brand/model selection.
 *
 * Built on shadcn Command (cmdk) + Popover. Features:
 *  - Type-to-search with built-in fuzzy filter (cmdk handles it natively;
 *    cmdk is extremely fast — handles 5000+ items without virtualization)
 *  - Keyboard navigation (Arrow Up/Down, Enter, Esc)
 *  - Sticky search input
 *  - Optional "recent" + "popular" sections shown when search is empty
 *  - Mobile-friendly (full-screen friendly popover, large touch targets)
 *  - "Other / free-text" sentinel for unknown items (free-text fallback)
 *
 * Why not react-window? cmdk filters in-memory; for ≤5000 items the DOM
 * is fine. We use `max-h` + native overflow-y for scrolling.
 *
 * Usage:
 *   <SearchableCombobox
 *     value={brand}
 *     onChange={setBrand}
 *     options={BRANDS}                  // string[] of valid options
 *     recent={["BMW","Audi"]}           // optional, shown above main list
 *     popular={["VW","Toyota","Tesla"]} // optional, shown when search empty
 *     placeholder="Marke wählen…"
 *     searchPlaceholder="Marke suchen…"
 *     emptyMessage="Keine Treffer"
 *     otherLabel="Andere Marke…"        // null/undefined = no other option
 *     onOther={() => openFreeTextInput()}
 *     testId="brand-select"
 *   />
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, History, Sparkles, Plus } from "lucide-react";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

export default function SearchableCombobox({
  value,
  onChange,
  options = [],
  recent = [],
  popular = [],
  placeholder = "Wählen…",
  searchPlaceholder = "Suchen…",
  emptyMessage = "Keine Treffer",
  otherLabel = null,
  onOther = null,
  disabled = false,
  testId = "searchable-combobox",
  className = "",
  popularLabel = "Häufig gewählt",
  recentLabel = "Zuletzt",
  allLabel = "Alle",
  triggerClassName = "border-zinc-800 bg-zinc-950 text-white",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Debounce search re-renders for large lists (>1000 items)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 80);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  // Reset search on close
  useEffect(() => { if (!open) setSearch(""); }, [open]);

  // Filter options (cmdk does its own filtering; we feed it everything)
  const recentSet = useMemo(() => new Set(recent || []), [recent]);
  const popularSet = useMemo(() => new Set(popular || []), [popular]);
  const restOptions = useMemo(() => {
    // Hide items already shown in recent/popular from the main "all" group
    // (only when the search box is empty)
    if (debouncedSearch) return options;
    return options.filter((o) => !recentSet.has(o) && !popularSet.has(o));
  }, [options, recentSet, popularSet, debouncedSearch]);

  const handleSelect = useCallback((val) => {
    if (val === "__other__") {
      setOpen(false);
      if (onOther) onOther();
      return;
    }
    onChange?.(val);
    setOpen(false);
  }, [onChange, onOther]);

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal disabled:opacity-40",
            triggerClassName,
            className,
          )}
          data-testid={testId}
        >
          <span className={cn("truncate", !value && "text-zinc-500")}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] border-zinc-800 bg-zinc-950 p-0 text-white"
        align="start"
        sideOffset={4}
        data-testid={`${testId}-popover`}
      >
        <Command
          className="bg-zinc-950"
          // Don't shouldFilter when search empty so all groups remain visible
          shouldFilter={true}
        >
          {/* Sticky search input — Phase 68 fixes scroll-too-fast UX */}
          <div className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-950">
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              className="h-11 text-sm"
              data-testid={`${testId}-search-input`}
            />
          </div>
          <CommandList
            className="max-h-[300px] overscroll-contain"
            data-testid={`${testId}-list`}
            // Slow-scroll desktop wheel: dampen native fast-scroll for trackpads
            onWheel={(e) => {
              // Allow native scrolling within the list; just stop bubbling
              // so the page behind doesn't also scroll when reaching the edge.
              e.stopPropagation();
            }}
          >
            <CommandEmpty data-testid={`${testId}-empty`}>{emptyMessage}</CommandEmpty>
            {!debouncedSearch && recent.length > 0 && (
              <>
                <CommandGroup heading={recentLabel}>
                  {recent.map((opt) => (
                    <CommandItem
                      key={`recent-${opt}`}
                      value={opt}
                      onSelect={handleSelect}
                      className="cursor-pointer data-[selected=true]:bg-zinc-800"
                      data-testid={`${testId}-item-recent-${opt}`}
                    >
                      <History className="mr-2 h-3.5 w-3.5 text-zinc-500" />
                      <span className="flex-1 truncate">{opt}</span>
                      {value === opt && <Check className="ml-2 h-4 w-4 text-red-500" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {!debouncedSearch && popular.length > 0 && (
              <>
                <CommandGroup heading={popularLabel}>
                  {popular.map((opt) => (
                    <CommandItem
                      key={`popular-${opt}`}
                      value={opt}
                      onSelect={handleSelect}
                      className="cursor-pointer data-[selected=true]:bg-zinc-800"
                      data-testid={`${testId}-item-popular-${opt}`}
                    >
                      <Sparkles className="mr-2 h-3.5 w-3.5 text-amber-400" />
                      <span className="flex-1 truncate">{opt}</span>
                      {value === opt && <Check className="ml-2 h-4 w-4 text-red-500" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading={debouncedSearch ? undefined : allLabel}>
              {restOptions.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={handleSelect}
                  className="cursor-pointer data-[selected=true]:bg-zinc-800"
                  data-testid={`${testId}-item-${opt}`}
                >
                  <span className="flex-1 truncate">{opt}</span>
                  {value === opt && <Check className="ml-2 h-4 w-4 text-red-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {otherLabel && onOther && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__other__"
                    onSelect={handleSelect}
                    className="cursor-pointer text-zinc-300 data-[selected=true]:bg-zinc-800"
                    data-testid={`${testId}-item-other`}
                  >
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    {otherLabel}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
